import { openDB, deleteDB } from 'idb';
import {
  LOCAL_DB_NAME,
  LOCAL_DB_STORE_NAMES,
  LOCAL_DB_VERSION,
  upgradeLocalDb,
} from './schema';

let dbPromise = null;
let dbAvailable = false;
let lastInitAt = null;
let lastError = null;
let storageExhausted = false;

const STORAGE_EXHAUSTED_KEY = 'ewu_local_db_storage_exhausted';

function nowIso() {
  return new Date().toISOString();
}

function isIndexedDbAvailable() {
  return typeof window !== 'undefined' && typeof indexedDB !== 'undefined';
}

function isKnownStore(storeName) {
  return LOCAL_DB_STORE_NAMES.includes(storeName);
}

function createUnavailableError() {
  return new Error('IndexedDB is not available in this browser context.');
}

function logLocalDbError(message, error) {
  lastError = error?.message || String(error);
  console.warn(message, error);
}

function isQuotaLikeError(error) {
  const name = String(error?.name || '').toLowerCase();
  const message = String(error?.message || error || '').toLowerCase();

  return (
    name.includes('quota')
    || name.includes('aborterror')
    || message.includes('quota')
    || message.includes('file_error_no_space')
    || message.includes('no space left on device')
    || message.includes('disk full')
    || message.includes('storage')
  );
}

function readPersistedStorageExhaustedFlag() {
  try {
    return typeof localStorage !== 'undefined' && localStorage.getItem(STORAGE_EXHAUSTED_KEY) === '1';
  } catch {
    return false;
  }
}

function persistStorageExhaustedFlag(exhausted) {
  try {
    if (typeof localStorage === 'undefined') return;

    if (exhausted) {
      localStorage.setItem(STORAGE_EXHAUSTED_KEY, '1');
    } else {
      localStorage.removeItem(STORAGE_EXHAUSTED_KEY);
    }
  } catch {
    // Ignore localStorage failures.
  }
}

function markStorageExhausted(error) {
  if (!isQuotaLikeError(error)) return;
  storageExhausted = true;
  persistStorageExhaustedFlag(true);
}

export function isLocalDbStorageExhausted() {
  return storageExhausted || readPersistedStorageExhaustedFlag();
}

export function clearLocalDbStorageExhaustedFlag() {
  storageExhausted = false;
  persistStorageExhaustedFlag(false);
}

async function safeDbOperation(operation, fallbackValue) {
  try {
    const db = await getLocalDb();
    if (!db) return fallbackValue;
    return await operation(db);
  } catch (error) {
    markStorageExhausted(error);
    logLocalDbError('EWU local DB operation failed:', error);
    return fallbackValue;
  }
}

export async function initLocalDb() {
  if (!isIndexedDbAvailable()) {
    dbPromise = null;
    dbAvailable = false;
    logLocalDbError('EWU local DB is unavailable:', createUnavailableError());
    return null;
  }

  if (!dbPromise) {
    dbPromise = openDB(LOCAL_DB_NAME, LOCAL_DB_VERSION, {
      upgrade(db, oldVersion, newVersion, transaction) {
        void newVersion;
        upgradeLocalDb(db, oldVersion, transaction);
      },
      blocked() {
        console.warn('EWU local DB upgrade is blocked by another open tab.');
      },
      blocking(currentVersion, blockedVersion, event) {
        void currentVersion;
        void blockedVersion;
        event?.target?.close?.();
        dbPromise = null;
        dbAvailable = false;
        console.warn('EWU local DB is blocking a newer version in another tab.');
      },
      terminated() {
        dbPromise = null;
        dbAvailable = false;
        console.warn('EWU local DB connection was terminated.');
      },
    });
  }

  try {
    const db = await dbPromise;
    dbAvailable = true;
    lastInitAt = nowIso();
    lastError = null;
    return db;
  } catch (error) {
    const isVersionError = String(error?.name || '').includes('VersionError')
      || String(error?.message || '').toLowerCase().includes('requested version');

    if (isVersionError) {
      try {
        dbPromise = openDB(LOCAL_DB_NAME);
        const db = await dbPromise;
        dbAvailable = true;
        lastInitAt = nowIso();
        lastError = null;
        return db;
      } catch (fallbackError) {
        dbPromise = null;
        dbAvailable = false;
        logLocalDbError('EWU local DB fallback initialization failed:', fallbackError);
        return null;
      }
    }

    dbPromise = null;
    dbAvailable = false;
    logLocalDbError('EWU local DB initialization failed:', error);
    return null;
  }
}

export async function getLocalDb() {
  return initLocalDb();
}

export async function clearLocalDb() {
  try {
    const db = await dbPromise;
    if (db) db.close();
  } catch (error) {
    logLocalDbError('EWU local DB close before clear failed:', error);
  }

  dbPromise = null;
  dbAvailable = false;
  clearLocalDbStorageExhaustedFlag();

  if (!isIndexedDbAvailable()) return false;

  try {
    await deleteDB(LOCAL_DB_NAME);
    lastInitAt = null;
    lastError = null;
    return true;
  } catch (error) {
    logLocalDbError('EWU local DB clear failed:', error);
    return false;
  }
}

export async function getObjectStoreCount(storeName) {
  if (!isKnownStore(storeName)) return 0;

  return safeDbOperation(async (db) => {
    return db.count(storeName);
  }, 0);
}

export async function saveRecord(storeName, record) {
  if (!isKnownStore(storeName) || !record) return null;
  if (isLocalDbStorageExhausted()) return null;

  try {
    const db = await getLocalDb();
    if (!db) return null;
    await db.put(storeName, record);
    return record;
  } catch (error) {
    markStorageExhausted(error);
    logLocalDbError('EWU local DB save failed:', error);
    return null;
  }
}

export async function getRecord(storeName, key) {
  if (!isKnownStore(storeName) || key === undefined || key === null) return null;

  return safeDbOperation(async (db) => {
    const record = await db.get(storeName, key);
    return record || null;
  }, null);
}

export async function deleteRecord(storeName, key) {
  if (!isKnownStore(storeName) || key === undefined || key === null) return false;

  return safeDbOperation(async (db) => {
    await db.delete(storeName, key);
    return true;
  }, false);
}

export async function listRecords(storeName, options = {}) {
  if (!isKnownStore(storeName)) return [];

  const {
    indexName,
    query,
    limit,
  } = options;

  return safeDbOperation(async (db) => {
    const tx = db.transaction(storeName, 'readonly');
    const source = indexName
      ? tx.store.index(indexName)
      : tx.store;

    if (!limit) {
      return source.getAll(query);
    }

    const records = [];
    let cursor = await source.openCursor(query);
    while (cursor && records.length < limit) {
      records.push(cursor.value);
      cursor = await cursor.continue();
    }
    return records;
  }, []);
}

export async function saveSyncState(key, value) {
  if (!key) return null;

  const record = {
    key,
    value,
    updated_at: nowIso(),
  };

  return saveRecord('sync_state', record);
}

export async function getSyncState(key) {
  if (!key) return null;

  const record = await getRecord('sync_state', key);
  return record ? record.value : null;
}

export async function addSyncLog(log = {}) {
  const record = {
    id: log.id || `${Date.now()}-${Math.random().toString(16).slice(2)}`,
    level: log.level || 'info',
    message: log.message || '',
    created_at: log.created_at || nowIso(),
    entity_type: log.entity_type || null,
    entity_id: log.entity_id || null,
    details: log.details || null,
  };

  return saveRecord('sync_logs', record);
}

export async function getLocalDbInfo() {
  const db = await initLocalDb();
  const storeCounts = {};

  const relevantStores = [
    'products',
    'tables',
    'orders',
    'pending_actions',
    'sync_logs',
  ];

  await Promise.all(relevantStores.map(async (storeName) => {
    storeCounts[storeName] = await getObjectStoreCount(storeName);
  }));

  return {
    name: LOCAL_DB_NAME,
    version: LOCAL_DB_VERSION,
    available: Boolean(db && dbAvailable),
    lastInitAt,
    lastError,
    storageExhausted: isLocalDbStorageExhausted(),
    storeNames: [...LOCAL_DB_STORE_NAMES],
    storeCounts,
  };
}

export {
  LOCAL_DB_NAME,
  LOCAL_DB_STORE_NAMES,
  LOCAL_DB_VERSION,
};
